import { animate } from "motion";
import type { Component, JSXElement } from "solid-js";
import { createSignal, Index, onCleanup, onMount, Show } from "solid-js";
import { twJoin } from "tailwind-merge";
import InfinityIcon from "~/Icons/24_Icon_Infinity.svg?component-solid";
import MeetingIcon from "~/Icons/24_Icon_Meeting.svg?component-solid";
import MidjourneyIcon from "~/Icons/24_Icon_Midjourney.svg?component-solid";
import NoVpnIcon from "~/Icons/24_Icon_NoVpn.svg?component-solid";
import RubblesIcon from "~/Icons/24_Icon_Rubles.svg?component-solid";
import TemplateIcon from "~/Icons/24_Icon_Template.svg?component-solid";
import AlfaIcon from "~/Icons/alfa.svg?component-solid";
import BBEIcon from "~/Icons/BBE.svg?component-solid";
import BHSDIcon from "~/Icons/BHSD.svg?component-solid";
import FonbetIcon from "~/Icons/fonbet.svg?component-solid";
import GroupIcon from "~/Icons/groupIcon.svg?component-solid";
import MtsIcon from "~/Icons/MTC_Logo_CMYK 1.svg?component-solid";
import PersonIcon from "~/Icons/personIcon.svg?component-solid";
import SberIcon from "~/Icons/sberIcon.svg?component-solid";
import StripeIcon from "~/Icons/stripeIcon.svg?component-solid";
import YandexIcon from "~/Icons/yandex.svg?component-solid";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { ProductCardV1, ProductCardV2 } from "~/solidJs/entities/PremiumPopUp/ui/ProductCard";
import { PromoCodeFeature } from "~/solidJs/features/PromoCodeFeature/PromoCodeFeature";
import type { ProductsApi } from "~/solidJs/shared/api/ProductsApi";
import { getFileSrcToCdnOrRelative, isUserFree, useCustomContext } from "~/solidJs/shared/helpers";
import { isMobile } from "~/solidJs/shared/helpers/deviceHelpers";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types";
import { Button, ButtonSwitcher, Switch, Tag } from "~/solidJs/shared/ui";
import { CopyBtn } from "~/solidJs/shared/ui/molecules/CopyBtn/CopyBtn";
import { getUserStore } from "../../HeaderMain/models";
import { tariffLokUp } from "../helpers/tariffLookUpModes";
import { PremiumPopUpContext } from "../models/premiumPopUpContext";
import type { TimeInterval } from "../types";

type PremiumProductBlocProps = {
	itemsToRender: Awaited<ReturnType<typeof ProductsApi.getProductList>>;
	isNeedToHideComments?: boolean;
	forceSelectedPlan?: string;
	trialSelectedPlan?: string;
	sourceGlobalId?: string;
	isHideRequestDemo?: boolean;
	scrollContainerRef: HTMLElement;
};
const _NewYearSection = () => {
	const [getIsShow, setIsShow] = createSignal(true);
	const promocode = "NY2025";
	let timeout: NodeJS.Timeout;
	const target = new Date(2025, 0, 2);
	const timeoutChecker = () => {
		const now = new Date();
		if (now.getTime() >= target.getTime()) {
			setIsShow(false);
			return;
		}
		timeout = setTimeout(timeoutChecker, 1000);
	};

	onMount(() => {
		timeout = setTimeout(timeoutChecker, 1000);
	});
	onCleanup(() => {
		clearTimeout(timeout);
	});
	return (
		<Show when={isUserFree(getUserStore()) && getIsShow()}>
			<div
				style={{
					"background-image": `url(${getFileSrcToCdnOrRelative("PremiumNewYearSectionBg.png")})`,
					"background-position": "center"
				}}
				class="relative h-64 w-full rounded-[30px] bg-blue-200 bg-cover bg-no-repeat"
			>
				<div class="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex flex-col items-center justify-center">
					<h1 class="bg-[#1C63EC] px-2 font-['Funnel_Sans'] font-medium text-[90px] text-white leading-[80px] tracking-[-3px]">
						50% OFF
					</h1>
					<h2 class="-translate-y-1 bg-[#1C63EC] p-2 text-center font-['Funnel_Sans'] font-medium text-[30px] text-white leading-[33px] tracking-[-1px]">
						on Your First Subscription
					</h2>
				</div>
				<div class="-translate-y-1/2 absolute top-1/2 right-5 flex flex-col items-center justify-center font-medium text-[#1C63EC] text-xl">
					<div class="bg-blue-200 px-2">Promocode</div>
					<CopyBtn
						onClick={() => {
							EventAnalytics.newYearEvents.tariffsCopyPromoCode();
							navigator.clipboard.writeText(promocode);
						}}
						class="rounded-full bg-[#1C63EC] px-9 py-3 font-['Funnel_Sans'] font-semibold text-[32px] text-white hover:bg-blue-700"
					>
						{promocode}
					</CopyBtn>
				</div>
			</div>
		</Show>
	);
};
export const PremiumProductBlocV2: Component<
	Omit<PremiumProductBlocProps, "scrollContainerRef">
> = (props) => {
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	return (
		<section class="flex w-full flex-col items-center gap-7">
			<div class="flex w-full flex-col items-center gap-3 overflow-hidden">
				<h1 class={"header-30-semibold text-center text-text-primary"}>
					You’re using a legacy plan
				</h1>
				<p class="caption-12-medium max-w-xl text-center text-text-secondary-v2">
					You’re on an outdated plan that is no longer available to new users. You can
					continue using it, or switch to a new pricing plan below.
				</p>
			</div>
			<div class="flex w-full flex-col gap-16">
				<div class="flex w-full flex-col gap-4">
					<ProductsSectionV2
						maxFeatureLength={props.itemsToRender.maxFeatureLength}
						itemsToRender={props.itemsToRender}
						forceSelectedPlan={props.forceSelectedPlan}
						trialSelectedPlan={props.trialSelectedPlan}
						sourceGlobalId={props.sourceGlobalId}
					/>
					<EnterpriseBlockV2 />
				</div>
				<Show when={getPremiumPopUpStore().isRuCards}>
					<TestimonialsV2 />
				</Show>
			</div>
		</section>
	);
};

export const EnterpriseBlockV2 = () => {
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	return (
		<div class="pricing-card grid grid-cols-2 rounded-2xl px-4 pt-3 pb-4">
			<div class="flex flex-col justify-between gap-8">
				<div class="flex flex-col">
					<div class="font-medium text-lg text-text-primary">Teams</div>
					<div class="font-medium text-sm text-text-secondary">
						Full access for all tools
					</div>
				</div>
				<div class="flex flex-col gap-4">
					<div
						class={twJoin(
							"flex items-center gap-4",
							!getPremiumPopUpStore().isRuCards && "opacity-0"
						)}
					>
						<EnterpriseFeatures Icon={<NoVpnIcon />} Text={"Works without VPN"} />
						<EnterpriseFeatures
							Icon={<RubblesIcon />}
							Text={"Corporate billing available in Russia"}
						/>
					</div>
					<Button
						onClick={EventAnalytics.pricing.bookADemo}
						variant="SecondaryNeutral"
						component="a"
						target="_blank"
						href="https://airtable.com/appT508YoSwEs5Euq/pag6t1MPpFLxmYLMi/form"
						size="large"
						class="w-64 px-8 py-7 hover:bg-interaction-violet hover:text-interaction-violet-light"
					>
						Book a demo
					</Button>
				</div>
			</div>
			<div class="flex flex-col gap-1.5 pt-1.5">
				<div class="font-semibold text-sm text-text-primary leading-4">
					<div class="inline font-bold">Includes all features</div>, and:
				</div>
				<div class="flex flex-col items-start">
					<EnterpriseItemPricing
						Icon={<MidjourneyIcon class="m-[2px] rounded" />}
						Text={
							<div class="rounded bg-interaction-violet px-1 text-white text-xs leading-4">
								Midjourney
							</div>
						}
					/>
					<EnterpriseItemPricing Icon={<InfinityIcon />} Text={"Unlimited generations"} />
					<EnterpriseItemPricing
						Icon={<MeetingIcon />}
						Text={"Personalized onboarding course from 5 seats"}
					/>
					<EnterpriseItemPricing
						Icon={<TemplateIcon />}
						Text={"Personalized template for 6 months subscription"}
					/>
				</div>
			</div>
		</div>
	);
};

export const TestimonialsV2 = () => {
	return (
		<div class="flex w-full flex-col items-center gap-4">
			<div class="flex items-center font-medium text-text-secondary-v2 text-xs leading-5">
				Trusted by brands and creators
			</div>
			<div class="flex items-center gap-8">
				<YandexIcon />
				<AlfaIcon />
				<BBEIcon />
				<FonbetIcon />
				<BHSDIcon />
				<MtsIcon />
			</div>
		</div>
	);
};

const EnterpriseItemPricing: Component<{ Icon: JSXElement; Text: JSXElement }> = (props) => {
	return (
		<div class="flex items-center gap-1.5 py-1.5">
			{props.Icon}
			<div class="-tracking-[0.065px] flex items-center font-medium text-text-secondary-v2 text-xs leading-4">
				{props.Text}
			</div>
		</div>
	);
};
const EnterpriseFeatures: Component<{ Icon: JSXElement; Text: JSXElement }> = (props) => {
	return (
		<div class="flex items-center gap-1.5">
			{props.Icon}
			<div class="-tracking-[0.065px] flex items-center font-medium text-background-violet text-sm leading-4">
				{props.Text}
			</div>
		</div>
	);
};
export const PremiumProductBlocV1: Component<PremiumProductBlocProps> = (props) => {
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	const expandCallbacks: (() => void)[] = [];
	const collapseCallbacks: (() => void)[] = [];
	let headRef: HTMLDivElement;
	let targetRef: HTMLElement;
	let statusEl: HTMLDivElement;
	const _animationState: "expanded" | "collapsed" = "expanded";
	let isAnimationInProgress = false;
	const [getAnimationState, setAnimationState] = createSignal<"expanded" | "collapsed">(
		"expanded"
	);
	const expandAnimation = async () => {
		if (getAnimationState() === "expanded") {
			return;
		}

		setAnimationState("expanded");
		for (const cb of expandCallbacks) {
			cb();
		}
		isAnimationInProgress = true;
		await animate(headRef, {
			height: [null, "initial"],
			opacity: [null, 1]
		});
		isAnimationInProgress = false;
	};
	const collapseAnimation = async () => {
		if (getAnimationState() === "collapsed") {
			return;
		}

		setAnimationState("collapsed");
		for (const cb of collapseCallbacks) {
			cb();
		}
		isAnimationInProgress = true;

		await animate(headRef, {
			height: [null, 0],
			opacity: [1, 0]
		});

		isAnimationInProgress = false;
	};
	const onObserve: IntersectionObserverCallback = (entries) => {
		if (isAnimationInProgress) {
			return;
		}
		const status = entries[0];
		if (status.isIntersecting) {
			expandAnimation();
			return;
		}
		collapseAnimation();
	};
	const observer = new IntersectionObserver(onObserve);
	onMount(() => {
		observer.observe(statusEl);
		onCleanup(() => {
			observer.disconnect();
		});
	});
	const registerOnExpand = (cb: () => void) => {
		expandCallbacks.push(cb);
	};
	const registerOnCollapse = (cb: () => void) => {
		collapseCallbacks.push(cb);
	};
	return (
		<>
			<div class="absolute top-0" ref={statusEl!} />
			<section
				ref={targetRef!}
				class="sticky top-0 z-10 flex w-full flex-col items-center gap-2"
			>
				<div ref={headRef!} class="flex w-full flex-col items-center gap-2 overflow-hidden">
					<h1 class={"m-0 font-semibold text-3xl"}>
						{getPremiumPopUpStore().isRuCards
							? "Choose plan to pay"
							: "Choose Your Ideal Plan"}
					</h1>
					<PromoCodeFeature />
					<PaymentMethodSwitchBlockV1 />
				</div>
				<div
					class={twJoin(
						"flex w-full gap-6 rounded-2xl transition-colors duration-500",
						getAnimationState() === "collapsed" && "bg-[#F3F4F6]"
					)}
				>
					<TeamsSection
						registerOnExpand={registerOnExpand}
						registerOnCollapse={registerOnCollapse}
						scrollContainerRef={props.scrollContainerRef}
					/>
					<ProductsSectionV1
						scrollContainerRef={props.scrollContainerRef}
						itemsToRender={props.itemsToRender}
						forceSelectedPlan={props.forceSelectedPlan}
						trialSelectedPlan={props.trialSelectedPlan}
						registerOnExpand={registerOnExpand}
						sourceGlobalId={props.sourceGlobalId}
						registerOnCollapse={registerOnCollapse}
					/>
				</div>
			</section>
		</>
	);
};

type ProductsSectionPropsV2 = {
	itemsToRender: Awaited<ReturnType<typeof ProductsApi.getProductList>>;
	forceSelectedPlan?: string;
	trialSelectedPlan?: string;
	maxFeatureLength: number;
	sourceGlobalId?: string;
};
type ProductsSectionPropsV1 = {
	scrollContainerRef: HTMLElement;
	itemsToRender: Awaited<ReturnType<typeof ProductsApi.getProductList>>;
	forceSelectedPlan?: string;
	trialSelectedPlan?: string;
	registerOnExpand?: (cb: () => void) => void;
	registerOnCollapse?: (cb: () => void) => void;
	sourceGlobalId?: string;
};

const ProductsSectionV2: Component<ProductsSectionPropsV2> = (props) => {
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	return (
		<div class="grid w-full grid-cols-4 items-start justify-center gap-4">
			<Index
				each={
					props.itemsToRender.productList[getPremiumPopUpStore().activeInterval].products
				}
			>
				{(item) => (
					<ProductCardV2
						selectedPlan={props.forceSelectedPlan}
						isTrial={props.trialSelectedPlan === item().nameId}
						sourceGlobalId={props.sourceGlobalId}
						maxFeatureLength={props.maxFeatureLength}
						btnMode={
							tariffLokUp[
								getUserStore()?.group || UserSubscriptionPlanEnum.Legacy_Freemium
							]?.[item().nameId]
						}
						{...item()}
					/>
				)}
			</Index>
		</div>
	);
};
const ProductsSectionV1: Component<ProductsSectionPropsV1> = (props) => {
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);

	let headRef: HTMLDivElement;
	onMount(() => {
		props.registerOnExpand?.(() => {
			animate(headRef, {
				height: [null, "initial"],
				opacity: [null, "1"]
			});
		});
		props.registerOnCollapse?.(() => {
			animate(headRef, { height: [null, 0], opacity: [1, 0] });
		});
	});

	return (
		<div
			class={
				"flex w-full flex-col items-start justify-start gap-2 rounded-2xl bg-[#F3F4F6] p-6 py-2"
			}
			style={{
				...(isMobile()
					? {
							"transform-origin": "center top"
						}
					: {})
			}}
		>
			<div ref={headRef!} class="flex w-full items-center justify-center gap-4">
				<h3 class="font-semibold">For individual</h3>
				<PersonIcon class="size-4" />
			</div>
			<div class="grid w-full grid-cols-4 items-start justify-center gap-2">
				<Index
					each={
						props.itemsToRender.productList[getPremiumPopUpStore().activeInterval]
							.products
					}
				>
					{(item) => (
						<ProductCardV1
							selectedPlan={props.forceSelectedPlan}
							isTrial={props.trialSelectedPlan === item().nameId}
							sourceGlobalId={props.sourceGlobalId}
							scrollContainerRef={props.scrollContainerRef}
							btnMode={
								tariffLokUp[
									getUserStore()?.group ||
										UserSubscriptionPlanEnum.Legacy_Freemium
								]?.[item().nameId]
							}
							registerOnExpand={props.registerOnExpand}
							registerOnCollapse={props.registerOnCollapse}
							{...item()}
						/>
					)}
				</Index>
			</div>
		</div>
	);
};

type TeamsSectionProps = {
	scrollContainerRef: HTMLElement;
	registerOnExpand?: (cb: () => void) => void;
	registerOnCollapse?: (cb: () => void) => void;
};
const TeamsSection: Component<TeamsSectionProps> = (props) => {
	let headRef: HTMLDivElement;
	const [getState, setState] = createSignal<"expanded" | "collapsed">("expanded");

	onMount(() => {
		props.registerOnExpand?.(() => {
			animate(headRef, {
				height: [null, "initial"],
				opacity: [null, "1"]
			});
			setState("expanded");
		});
		props.registerOnCollapse?.(() => {
			animate(headRef, { height: [null, 0], opacity: [1, 0] });
			setState("collapsed");
		});
	});
	return (
		<div
			class={
				"relative flex w-full min-w-48 max-w-64 flex-col items-start justify-start gap-2 rounded-2xl bg-[#F3F4F6] p-6 py-2"
			}
		>
			<h1
				class={twJoin(
					"-translate-y-1/2 absolute top-1/2 left-0 m-0 p-6 font-semibold text-3xl text-text-primary opacity-0 transition-opacity duration-500",
					getState() === "collapsed" && "opacity-100"
				)}
			>
				Compare Individual Plans
			</h1>
			<div ref={headRef!} class="flex w-full items-center justify-center gap-4">
				<h3 class="font-semibold">For Teams</h3>
				<GroupIcon class="size-4" />
			</div>
		</div>
	);
};

const PremiumMethodSwitcherFeatureV2: Component = () => {
	const { getPremiumPopUpStore, setPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);

	const togglePaymentMethod = (isRuCards: boolean) => {
		setPremiumPopUpStore((prev) => ({ ...prev, isRuCards: isRuCards }));
		EventAnalytics.premiumPopUp.switchPaymentMethod(isRuCards);
	};

	return (
		<div class="flex rounded-full bg-interaction-violet-light p-0.5">
			<ButtonSwitcher
				values={[
					{ value: "yoo", Icon: SberIcon },
					{ value: "stripe", Icon: () => <StripeIcon class="w-[103px]" /> }
				]}
				activeIndex={getPremiumPopUpStore().isRuCards ? 0 : 1}
			>
				{(dropDownProps) => (
					<Button
						data-test={"premium-switch-method"}
						data-test-method={`premium-switch-method-${dropDownProps.item().value}`}
						variant={"plain"}
						class={twJoin(
							"flex h-8 items-center gap-2 rounded-full px-4 font-semibold",
							dropDownProps.isSelected()
								? "bg-interaction-violet text-white"
								: "text-text-secondary-v2"
						)}
						onClick={() => {
							togglePaymentMethod(dropDownProps.item().value === "yoo");
						}}
					>
						{dropDownProps.item().Icon({})}
					</Button>
				)}
			</ButtonSwitcher>
		</div>
	);
};
const PremiumMethodSwitcherFeatureV1: Component = () => {
	const { getPremiumPopUpStore, setPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);

	const togglePaymentMethod = (isRuCards: boolean) => {
		setPremiumPopUpStore((prev) => ({ ...prev, isRuCards: isRuCards }));
		EventAnalytics.premiumPopUp.switchPaymentMethod(isRuCards);
	};

	return (
		<div class="flex items-center gap-2">
			<SberIcon
				onClick={() => togglePaymentMethod(true)}
				data-test={"premium-switch-method"}
				data-test-method={"premium-switch-method-yoo"}
				class={twJoin(
					getPremiumPopUpStore().isRuCards
						? "fill-interaction-violet [&>path]:fill-interaction-violet"
						: "fill-gray-500 [&>path]:fill-gray-500"
				)}
			/>
			<Switch
				size={"md"}
				checked={!getPremiumPopUpStore().isRuCards}
				colorStyle="purple"
				onCheckedChange={(e) => {
					togglePaymentMethod(!e.checked);
				}}
				id={"swtichPaymentMethod"}
			/>
			<StripeIcon
				onClick={() => togglePaymentMethod(false)}
				data-test={"premium-switch-method"}
				data-test-method={"premium-switch-method-stripe"}
				class={twJoin(
					"w-32",
					getPremiumPopUpStore().isRuCards
						? "fill-gray-500 [&>path]:fill-gray-500"
						: "fill-interaction-violet [&>path]:fill-interaction-violet"
				)}
			/>
		</div>
	);
};

const _PaymentMethodSwitchBlockV2: Component = () => {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 font-medium text-text-secondary-v2 text-xs">
			<div class="-tracking-[0.065px] text-[13px] leading-4">Pay with</div>
			<div class="relative flex w-full items-center justify-center px-4">
				<PremiumMethodSwitcherFeatureV2 />
				<div class="absolute right-0">
					<SwitchPaymentIntervalV2 />
				</div>
			</div>
		</div>
	);
};
const PaymentMethodSwitchBlockV1: Component = () => {
	return (
		<div class="flex w-full items-center justify-between px-4">
			<PremiumMethodSwitcherFeatureV1 />
			<SwitchPaymentIntervalV1 />
		</div>
	);
};
const SwitchPaymentIntervalV1: Component = () => {
	const { getPremiumPopUpStore, setPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	const switchActiveInterval = (interval: TimeInterval) => {
		EventAnalytics.premiumPopUp.premiumBannerChangePeriod(interval);
		setPremiumPopUpStore((prev) => ({
			...prev,
			activeInterval: interval
		}));
	};
	return (
		<div class="flex items-center gap-4">
			<div
				onClick={() => switchActiveInterval("1mo")}
				class={twJoin(
					getPremiumPopUpStore().activeInterval === "1mo"
						? "text-interaction-violet"
						: "text-gray-500"
				)}
				data-test-period={"premium-switch-period-short"}
				data-test={"premium-switch-period"}
			>
				1 Month
			</div>
			<Switch
				size={"md"}
				checked={getPremiumPopUpStore().activeInterval === "12mo"}
				colorStyle="purple"
				onCheckedChange={(e) => {
					switchActiveInterval(e.checked ? "12mo" : "1mo");
				}}
			/>
			<div
				onClick={() => switchActiveInterval("12mo")}
				class={twJoin(
					"flex items-center gap-2",
					getPremiumPopUpStore().activeInterval === "12mo"
						? "text-interaction-violet"
						: "text-gray-500"
				)}
				data-test-period={"premium-switch-period-long"}
				data-test={"premium-switch-period"}
			>
				1 Year
				<Tag size="lg" variant="premium" class="text-interaction-violet">
					Save 20%
				</Tag>
			</div>
		</div>
	);
};
const SwitchPaymentIntervalV2: Component = () => {
	const { getPremiumPopUpStore, setPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	const switchActiveInterval = (interval: TimeInterval) => {
		EventAnalytics.premiumPopUp.premiumBannerChangePeriod(interval);
		setPremiumPopUpStore((prev) => ({
			...prev,
			activeInterval: interval
		}));
	};
	return (
		<div class="flex rounded-full bg-background-gray-medium p-0.5">
			<ButtonSwitcher
				values={
					[
						{ value: "1mo", label: "Monthly" },
						{ value: "12mo", label: "Yearly" }
					] as const
				}
				activeIndex={["1mo", "12mo"].indexOf(getPremiumPopUpStore().activeInterval)}
			>
				{(drodDownProps) => (
					<Button
						data-test={"premium-switch-period"}
						data-test-period={`premium-switch-period-${drodDownProps.item().value === "1mo" ? "short" : "long"}`}
						variant={"plain"}
						class={twJoin(
							"flex h-8 items-center gap-2 rounded-full px-3 font-medium text-[13px]",
							drodDownProps.isSelected()
								? "bg-white text-interaction-violet"
								: "text-text-secondary-v2"
						)}
						onClick={() => {
							switchActiveInterval(drodDownProps.item().value);
						}}
					>
						<div>{drodDownProps.item().label}</div>
						<Show when={drodDownProps.item().value === "12mo"}>
							<div class="rounded-full bg-interaction-violet-light p-1 text-[10px] text-interaction-violet">
								–20%
							</div>
						</Show>
					</Button>
				)}
			</ButtonSwitcher>
		</div>
	);
};
